/**
 * Chart prefetch arguments for SSR openings. Rolling queries
 * share a duration-based SDK key; their REST bounds resolve at fetch time.
 * Fixed terminal history keeps exact creation/resolution bounds.
 */

import { exclusiveChartRequestEnd, LIVE_WINDOW_SECONDS } from './chart-factory';
import { resolveChartRequestStart } from './chart-window-boundaries';
import type {
  ChartInterval,
  ChartMode,
  ConcreteChartInterval,
  RenderMode,
  YAxisProperty,
} from './types';

/** Frontend `YAxisProperty` → backend `property` parameter.
 *  `'marketCap'` maps to effective market cap, not raw pool reserves. */
export function toBackendProperty(prop: YAxisProperty): string {
  if (prop === 'marketCap') return 'market_cap';
  return prop;
}

/** Frontend `RenderMode` → backend `chartType` parameter. */
export function toBackendChartType(renderMode: RenderMode): 'line' | 'ohlcv' {
  if (renderMode === 'candle') return 'ohlcv';
  return 'line';
}

/** Legacy chart-request shape retained for request-planning regressions. */
export interface ChartPrefetchArgs {
  readonly pubkey: string;
  readonly outcomeIndex: string;
  readonly interval: ConcreteChartInterval | 'all';
  readonly property: string;
  readonly chartType: 'line' | 'ohlcv';
  readonly liveWindowSeconds?: string;
  readonly from?: string;
  readonly to?: string;
  readonly targetBars?: number;
}

export interface ResolveChartPrefetchArgsInput {
  readonly marketPubkey: string;
  readonly outcomeCount?: number;
  /** Exact outcome indexes when the surface renders a subset (winner pages). */
  readonly outcomeIndices?: readonly number[];
  /**
   * Optional per-market `MarketConfig.max_outcomes`. When provided, the
   * prefetch fan-out is clamped to `min(outcomeCount, maxOutcomes)`; when
   * omitted, `outcomeCount` is trusted unchanged.
   */
  readonly maxOutcomes?: number;
  /** Exact market creation in Unix seconds. Unknown identity skips prefetch. */
  readonly createdAtUnix: number | undefined;
  /** Fixed request right edge. Defaults to the current minute. */
  readonly toUnix?: number;
  /** Exact immutable right edge for a resolved market's All-history request. */
  readonly terminalAtUnix?: number;
  readonly chartMode: ChartMode;
  readonly yAxisProperty: YAxisProperty;
  readonly renderMode: RenderMode;
  readonly interval: ChartInterval;
  /** Historical seconds needed by a live-mode seed query. */
  readonly liveWindowSeconds?: number;
}

/**
 * Build the per-outcome prefetch arg list for a given market + user chart
 * settings. Returns one entry per outcome up to MAX_OUTCOME_SLOTS. Output is
 * keyed by duration for rolling history and exact bounds for fixed history.
 */
export function resolveChartPrefetchArgs(
  input: ResolveChartPrefetchArgsInput,
): readonly ChartPrefetchArgs[] {
  const safeOutcomeCount = Math.max(0, input.outcomeCount ?? 0);
  const candidates =
    input.outcomeIndices ?? Array.from({ length: safeOutcomeCount }, (_, index) => index);
  const outcomeIndices = [
    ...new Set(
      candidates.filter(
        (index) =>
          Number.isInteger(index) &&
          index >= 0 &&
          (input.maxOutcomes === undefined || index < Math.max(0, input.maxOutcomes)),
      ),
    ),
  ];
  if (outcomeIndices.length === 0 || input.createdAtUnix === undefined) return [];

  const currentUnix = Math.floor(Date.now() / 1000);
  const nowUnix = Math.floor(currentUnix / 60) * 60;
  const terminalAtUnix =
    input.terminalAtUnix !== undefined && Number.isFinite(input.terminalAtUnix)
      ? input.terminalAtUnix
      : undefined;
  const toUnix = Math.min(
    terminalAtUnix ?? input.toUnix ?? nowUnix,
    terminalAtUnix === undefined ? nowUnix : currentUnix,
  );
  const createdAtUnix = Math.min(
    input.createdAtUnix,
    input.interval === 'all' && terminalAtUnix === undefined ? currentUnix : toUnix,
  );
  const lifespanSeconds = Math.max(0, toUnix - createdAtUnix);
  const liveWindowSeconds = input.liveWindowSeconds ?? LIVE_WINDOW_SECONDS;

  // Historical lookbacks use bounded `all`, so the backend chooses a bucket
  // for the visible span. These bounds must match the client's request or the
  // dehydrated SSR cache will miss.
  const outputInterval: ConcreteChartInterval | 'all' = input.interval === 'live' ? '1m' : 'all';

  // Open `all` carries the trading start so proposal age cannot coarsen it.
  // Terminal `all` carries exact creation/resolution bounds so a graduated
  // winner's later AMM history cannot enter the market-detail cache.
  const createdAtMs = createdAtUnix * 1000;
  const toMs = toUnix * 1000;
  const terminalAll = input.interval === 'all' && terminalAtUnix !== undefined;
  const from =
    input.interval === 'live'
      ? new Date(Math.max(createdAtMs, toMs - liveWindowSeconds * 1000)).toISOString()
      : resolveChartRequestStart({
          interval: input.interval,
          marketCreatedAt: createdAtUnix,
          anchorMs: toMs,
          lifespanSecs: lifespanSeconds,
        });
  const to = terminalAll
    ? new Date(toMs).toISOString()
    : input.interval === 'all'
      ? undefined
      : exclusiveChartRequestEnd(toMs);

  // Property/chart type derivation mirrors the connected client. Price and
  // market-cap candles are both canonical backend OHLC series.
  const chartType: 'line' | 'ohlcv' =
    input.chartMode === 'live' && toBackendChartType(input.renderMode) === 'ohlcv'
      ? 'ohlcv'
      : 'line';
  const property =
    input.chartMode === 'consensus' ? 'twar' : toBackendProperty(input.yAxisProperty);
  const args: ChartPrefetchArgs[] = [];
  for (const outcomeIndex of outcomeIndices) {
    args.push({
      pubkey: input.marketPubkey,
      outcomeIndex: String(outcomeIndex),
      interval: outputInterval,
      property,
      chartType,
      ...(input.interval === 'live'
        ? {
            liveWindowSeconds: String(liveWindowSeconds),
          }
        : { targetBars: 180 }),
      ...(from !== undefined ? { from } : {}),
      ...(to !== undefined ? { to } : {}),
    });
  }
  return args;
}
